import { filter, includes } from 'lodash';

import { DeviceOS } from 'enums';
import { Deeplink, Requisites } from 'types';

// an unknown OS is most likely a phone with an unrecognised UA, which can tap
// a button but cannot scan its own screen, so it gets both ways to pay
const getSqDeeplinkDisplayMode = (deviceOS: DeviceOS | '') => ({
  showButtons:
    includes([DeviceOS.Android, DeviceOS.IOS], deviceOS) || deviceOS === '',
  showQr: deviceOS === DeviceOS.Desktop || deviceOS === '',
});

const URL_ENCODE_TAG = /\[(\/?)URL_ENCODE\]/g;

// Block structure is taken from the template before substitution, so a requisite
// value containing a tag cannot open or close a block. Returns null when a block is
// left unclosed or a closing tag has no opening one.
const renderUrlEncodeBlocks = (
  template: string,
  substitute: (text: string) => string,
): string | null => {
  const levels = [''];
  let lastIndex = 0;

  for (const match of Array.from(template.matchAll(URL_ENCODE_TAG))) {
    levels[levels.length - 1] += substitute(
      template.slice(lastIndex, match.index),
    );
    lastIndex = (match.index ?? 0) + match[0].length;

    if (!match[1]) {
      levels.push('');
      continue;
    }
    if (levels.length === 1) {
      return null;
    }
    const content = levels.pop() as string;
    levels[levels.length - 1] += encodeURIComponent(content);
  }

  if (levels.length !== 1) {
    return null;
  }
  return levels[0] + substitute(template.slice(lastIndex));
};

// an order carries either a card or a phone, so the template for the other one
// would render with an empty recipient and open the bank app on a blank screen
const isFillable = (url?: string, requisites?: Requisites): boolean =>
  (!includes(url, '{{card}}') || !!requisites?.cardInfo) &&
  (!includes(url, '{{phone}}') || !!requisites?.phone) &&
  (!includes(url, '{{account_number}}') || !!requisites?.accountNumber) &&
  (!includes(url, '{{cardholder}}') || !!requisites?.cardholder?.trim()) &&
  renderUrlEncodeBlocks(url ?? '', (text) => text) !== null;

const getMatchingDeeplinks = ({
  deeplinks,
  customerBank,
  deviceOS,
  requisites,
  includeAllOsOnDesktop,
}: {
  deeplinks: Deeplink[] | undefined;
  customerBank?: string;
  deviceOS: DeviceOS | '';
  requisites?: Requisites;
  // desktop cannot open any of these links itself, so a non-empty result only
  // signals that a QR handoff is worth offering; opt-in because most consumers
  // (Default template, the deeplink page itself) must keep strict OS filtering
  includeAllOsOnDesktop?: boolean;
}): Deeplink[] =>
  filter(
    deeplinks,
    (deeplink) =>
      !!deeplink.url &&
      (!customerBank || deeplink.customerBank === customerBank) &&
      ((includeAllOsOnDesktop && deviceOS === DeviceOS.Desktop) ||
        !deeplink.deviceOs ||
        deeplink.deviceOs === deviceOS) &&
      isFillable(deeplink.url, requisites),
  );

export const deeplinkUtils = {
  isFillable,
  renderUrlEncodeBlocks,
  getMatchingDeeplinks,
  getSqDeeplinkDisplayMode,
};
